import Helper from '../../../models/Helper';

const URI = '/courses/:courseId/chapters/:chapterId';

/**
 * @desc Helper for chapters.
 */
class ChaptersHelper extends Helper {
  constructor(api) {
    super(api, URI);
  }

  /**
   * @desc Get chapter of given course.
   * @param {Number} courseId Id of the course to fetch.
   * @param {Number} chapterId Id of the chapter to get.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise<Chapter>|Request}
   * The promise handling the request if `directlyExecute` is `true`, the new created
   * request otherwise.
   * @example
   * const courseId = 1234;
   * const chapterId = 5678;
   * const response = await myClient.helpers.learningContent.chapters.get(courseId, chapterId);
   * @example
   * const courseId = 1234;
   * const chapterId = 5678;
   * const directlyExecute = false
   * const response = await myClient.helpers.learningContent
   *                    .chapters.get(courseId, chapterId, directlyExecute);
   * // Manipulate request object here...
   * const response = await request.execute();
   */
  get(courseId, chapterId, directlyExecute = true) {
    const request = this.api.createGetRequest(this.uri).setRouteParams({ courseId, chapterId });

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @desc Get content of specific chapter.
   * @param {Number} courseId Id of the course to fetch.
   * @param {Number} chapterId Id of the chapter to get.
   * @return {Promise<String>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const chapterId = 5678;
   * const response = await myClient.helpers.learningContent
   *                    .chapters.getContent(courseId, chapterId);
   */
  getContent(
    courseId: number,
    chapterId: number,
    queryParams?: {
      status?: string;
    }
  ) {
    const request = this.get(courseId, chapterId, false);
    request.setHeader('Accept', 'text/html');
    request.setHeader('Content-Type');

    request.url += '/content';

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    return request.execute();
  }

  /**
   * @desc Set the given chapter as completed
   * @param {Number} chapterId Id of the chapter to mark as completed
   * @returns {Promise} The promise handling the request
   */
  completeChapter(chapterId: number): Promise<any> {
    const request = this.api.createPostRequest('/chapters');

    request.url += '/:chapterId/complete';
    request.setRouteParams({ chapterId });

    return request.execute();
  }

  cancelCompleteChapter(chapterId: number): Promise<any> {
    const request = this.api.createDeleteRequest('/chapters');

    request.url += '/:chapterId/complete';
    request.setRouteParams({ chapterId });

    return request.execute();
  }
}

export default ChaptersHelper;
